import { useState, createContext, useContext, useCallback } from "react";
import i18next, { exists } from "i18next";
import { ITDContext, ConsoleLog, ModalManager, ModalProps } from "@inntechcorp/it-design";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import relativeTime from 'dayjs/plugin/relativeTime';
import { ConfigProvider } from "antd";
import { toast } from "sonner";

//import Socket from "./SocketContext";
import { INNError, usePostFetch } from "Api/hooks/usePostFetch";
import { LoginDataPropsType } from "./types/LoginDataPropsType";
import { DefaultLogin } from "./types/DefaultLogin";
import { GetLocalState, SetLocalState } from "Connectivity/LocalStorage";
import { BOContextPropsType } from "./types/BOContextPropsType";
import ModalData from "Helpers/modal/ModalData";
import { ChildrenProps } from "Types/ChildrenProps";
import { AdminTypes } from "Types/enums";

import { LoginResultPropsType } from "./types/LoginResultPropsType";
import { DateFormatPropsType } from "./types/DateFormatPropsType";
import { setActiveTimezoneCache } from "Helpers/date/timezone";

//import tr from 'antd/locale/tr_TR';
//import en from 'antd/locale/en_US';

import trLocale from 'antd/locale/tr_TR';
import enLocale from 'antd/locale/en_US';

import 'dayjs/locale/tr';
import 'dayjs/locale/en';

dayjs.extend(relativeTime);

const BackOfficeContext = createContext<BOContextPropsType>({} as BOContextPropsType);
      BackOfficeContext.displayName = "BackOfficeContext";
      
export const BackOfficeProvider = ({ children }:{children:ChildrenProps}) => {

  //const { listenSocket, cancelSocket } = Socket.useSocket();

  const { 
    changeLanguage,
    changeLocale, 
    changeDateFormat, 
    changeTheme 
  } = useContext(ITDContext);

  const { showModal, removeAllModals }          = ModalManager.useModal();
  const { fetch }                               = usePostFetch();

  const [login,             SetLogin]           = useState<LoginDataPropsType>(DefaultLogin);
  const [isLoggedIn,        SetLoggedIn]        = useState(false);
  const [isLoggingOut,      SetLoggingOut]      = useState(false);
  const [isOnline,          SetIsOnline]        = useState(false);
  const [showBodyScroll,    SetShowBodyScroll]  = useState(true);
  const [isSidebarOpen,     SetIsSidebarOpen]   = useState(true);
  const [theme,             SetTheme]           = useState("");
  const [flag,              SetFlag]            = useState("");
  const [language,          SetLanguage]        = useState("");
  const [locale,            SetLocale]          = useState("");
  const [currency,          SetCurrency]        = useState("");
  const [timezone,          SetTimeZone]        = useState("");
  const [dateFormat,        SetDateFormat]      = useState<DateFormatPropsType>({
    "Moment"        : "",
    "Date"          : "",
    "Time"          : "",
    "FullDate"      : "",
    "HumanDate"     : "",
    "DateTime"      : "",
    "FullDateTime"  : ""
  });

  const { t } = i18next;

  const addNotificationSuccess = useCallback((message = "", description = "") => {
    toast.success(
      exists(message) ? t(message) : message,
      {
        description : exists(description) ? t(description) : description,
      }
    );
  }, [t]);
  
  const addNotificationError = useCallback((message = "", description = "") => {
    toast.error(
      exists(message) ? t(message) : message,
      {
        description : exists(description) ? t(description) : description,
      }
    );
  }, [t]);

  /* ---------------------------------------- */
  
  const getLanguageDataBySlug = useCallback((slug:any) => { 
    return GetLocalState("languages").filter((l:any) => l.Slug === slug)[0];
  }, []);

  /* ---------------------------------------- */

  const ChangeLogin = async (
    data            : LoginResultPropsType | LoginDataPropsType | null = null,
    parseLogin      : boolean = true,
    defaultValues   : any = null
  ) => {

    if ( !data ) {
      console.error("Login data can't provided!");
      return;
    }

    if ( parseLogin && (data as LoginResultPropsType).profile_photo === "" ) {
      (data as LoginResultPropsType).profile_photo = GetLocalState("assets").icons.DefaultProfilePhoto;
    }
    
    const loginData = parseLogin ? getLoginDataByData(data as LoginResultPropsType) : data as LoginDataPropsType;
    
    SetLoggedIn(true);
    SetLogin(loginData);
    SetLocalState("login", loginData);

    /**
     * Set the currency to the currency of the login data.
     */
    ChangeCurrency(loginData.clientCurrencyCode || defaultValues.Currency);

    /**
     * Set the active brand timezone. All date displays and API filter ranges
     * across the panel read from this single value so every admin of the same
     * brand sees the same data in the same wall-clock time.
     *
     * If the login response does not include a brand timezone (e.g. the
     * backend hasn't shipped the field yet), fall back to the admin's
     * browser timezone so every downstream table/picker is still "ready"
     * and can issue API requests instead of hanging on the gate.
     */
    const resolvedTimezone = loginData.client?.timezone || dayjs.tz.guess() || "UTC";
    ChangeTimezone(resolvedTimezone);
    
    /*
    listenSocket(
      CoreConfig.Socket.Path,
      CoreConfig.Socket.Namespace,
      CoreConfig.Socket.Room,
      loginData.token
      );
    */
    
  };

  const ChangeLogout = useCallback((status = "", message?:string) => {

    ConsoleLog.single("🚨 ChangeLogout > Status: " + status);

    SetLoggingOut(true);
    removeAllModals();

    if ( status === "tokenIsExpired" || status === "userNotLoggedIn" ) {
      if ( message && Cookies.get("bo-token") !== undefined ) {
        addNotificationError(message);
      }
      RemoveLoginTokens();
      return;
    }

    else if ( 
      status === "badGateway" 
      || status === "error" 
      || status === "serviceTemporarilyUnavailable" 
      || status === "internalServerError" 
      || status === "noResponseFromApiPath" 
      || status === "unhandledError" 
    ) {
      RemoveLoginTokens();
      addNotificationError(`forms.notifications.${status}`);
      return;
    }

    fetch(
      "post",
      `/api/admin-be/admin/logout`,
      {},
      { 
        onFetched: () => {
          RemoveLoginTokens();
          addNotificationSuccess("forms.notifications.logoutSuccess")
        },
        onError: (err?: INNError):void => {
          console.error("err", err?.detail);
          RemoveLoginTokens();
          //addNotificationError("forms.notifications.logoutFailed")
        }
      }
    );

  }, [removeAllModals, addNotificationError, addNotificationSuccess, fetch]);

    /**
   * 
   * Ön tanımlı modal açmak için kullanılır. Config'ten gelen
   * modal değerlerine göre açar ve içeriğini belirler. Ön tanımlı
   * olmayan modal açmak için ITD kütüphanesindeki ModalManager 
   * kullanılması gereklidir.
   * 
   * @param {*} name Açılacak modal'ın adı
   * 
   */
  const ShowModal = (id:string, name:string, props?:Partial<ModalProps>) => {

    let modalProps:ModalProps = {
      id                    : id,
      title                 : "",
      width                 : "440px",
      closeOnClickOutside   : true,
      preventESC            : false,
      showOkButton          : true,
      showCancelButton      : true,
      showCloseButton       : true,
      okButtonText          : t("forms.buttons.OK"),
      cancelButtonText      : t("forms.buttons.cancel"),
      customProps           : props?.customProps ? props.customProps : {},
      onOk                  : null,
      onCancel              : null,
      content               : null,
      context               : null,
      okButtonProps         : null,
      cancelButtonProps     : null,
      removeOthers          : false,
      ...props
    }

    showModal(ModalData(name, modalProps, getContextValue));

  };

  const RemoveLoginTokens = useCallback(async () => {
    //cancelSocket();
    SetLocalState("login", {});
    Cookies.remove("bo-token");
    SetLogin(DefaultLogin);
    SetLoggedIn(false);
    SetLoggingOut(false);
  }, []);

  const UpdateLogin = useCallback(<K extends keyof LoginDataPropsType>(key: K, value: LoginDataPropsType[K]) => {
    SetLogin(prevLogin => {
      const data = {...prevLogin};
      data[key] = value;
      SetLocalState("login", data);
      return data;
    });
  }, []);

  const ChangeTheme = useCallback((value:string) => {
    Cookies.set("bo-theme", value);
    SetTheme(value);
    changeTheme(value);
  }, [changeTheme]);
  
  const ChangeFlag = useCallback((value:string) => {
    Cookies.set("bo-flag", value);
    SetFlag(value);
  }, []);

  const ChangeLocale = useCallback((value:string) => {
    Cookies.set("bo-locale", value);
    changeLocale(value);
    SetLocale(value);
  }, []);
  
  const ChangeLanguage = useCallback((value:string) => {

    SetLanguage(value);
    ChangeLocale(value);
    i18next.changeLanguage(value);
    changeLanguage(value);
    Cookies.set("bo-language", value);
    dayjs.locale(value);

    const format = getLanguageDataBySlug(value).Format;
    SetDateFormat(format);
    changeDateFormat(format.Moment);

  }, [changeLocale, changeLanguage, changeDateFormat, getLanguageDataBySlug]);

  const ChangeCurrency = useCallback((value:string) => {
    Cookies.set("bo-currency", value);
    SetCurrency(value);
  }, []);

  const ChangeTimezone = useCallback((value:string) => {
    if (!value) return;
    setActiveTimezoneCache(value);
    SetTimeZone(value);
  }, []);

  const ChangeDateFormat = useCallback((format:DateFormatPropsType) => {
    SetDateFormat(format);
  }, []);

  const ChangeIsSidebarOpen = useCallback((value:boolean) => {
    SetIsSidebarOpen(value);
  }, []);

  const ChangeIsOnline = (value:boolean) => {
    
    if ( value ) {
      ConsoleLog.single("📡 Connection restored. Internet connection active.",);
    }
    else {
      ConsoleLog.single("🕯️ Connection error. There is no Internet connection.", "error");
    }

    SetIsOnline(value);

    if ( !value ) ShowModal("disconnect-modal", "disconnect");
    
  };

  /* ---------------------------------------- */

  /*
  function wait(ms) {
    return new Promise( (resolve) => {setTimeout(resolve, ms)});
  }
  */

  const getLocale = useCallback(() => {
    if ( language === "tr" ) return trLocale;
    return enLocale;
  }, [language]);

  
  const getLoginDataByData = useCallback((data:LoginResultPropsType): LoginDataPropsType => {
    
    let _data: LoginDataPropsType = {
      type                  : data.admin.type as AdminTypes,
      isRoot                : data.admin.is_root,
      username              : data.admin.username,
      firstName             : data.admin.first_name,
      middleName            : data.admin.middle_name,
      lastName              : data.admin.last_name,
      language              : data.admin.language_code,
      forceChangePassword   : data.force_change_password,
      lastLoginCity         : data.location.city,
      lastLoginCountry      : data.location.country,
      clientCurrencyCode    : data.client_currency_code,
      clientLanguages       : data.client_language_codes ? data.client_language_codes : [],
      updateImportantFields : data.update_important_fields || [],
      permissions           : data.admin.role ? data.admin.role.permissions : [],
      clientEmailSender     : data.client_email_sender,
      clientCaptchaEnabled  : data.client_captcha_enabled,
      clientOtpEnabled      : data.client_otp_enabled,
      canExport             : data.can_export,
      token                 : data.token,
      profilePhoto          : data.profile_photo,
      device                : {
        browser   : data.device.browser,
        device    : data.device.device,
        isMobile  : data.device.is_mobile
      },
      location              : {
        city    : data.location.city,
        country : data.location.country
      },
      ip                    : data.ip,
      client: {
        id          : data.admin.client.id,
        name        : data.admin.client.name,
        slug        : data.admin.client.slug,
        siteName    : data.admin.client.site_name,
        domain      : data.admin.client.domain,
        timezone    : data.client_timezone || ""
      }
    }

    return _data;

  }, []);

  /* ---------------------------------------- */
  
  const getContextValue: BOContextPropsType = {
    ShowModal,
    ChangeLogout,
    RemoveLoginTokens,
    UpdateLogin,
    login,              ChangeLogin,
    theme,              ChangeTheme,
    flag,               ChangeFlag,
    language,           ChangeLanguage,
    locale,             ChangeLocale,
    currency,           ChangeCurrency,
    timezone,           ChangeTimezone,
    dateFormat,         ChangeDateFormat,
    isOnline,           ChangeIsOnline,
    isSidebarOpen,      ChangeIsSidebarOpen,
    showBodyScroll,     SetShowBodyScroll,
    isLoggedIn,
    isLoggingOut
  }

  return (
    <BackOfficeContext.Provider value={getContextValue}>
      <ConfigProvider locale={getLocale()}>
        {children}
      </ConfigProvider>
    </BackOfficeContext.Provider>
  );
};

export default BackOfficeContext;

